import { FC, useState } from 'react';
import { FormikHelpers } from 'formik';
import {
  AutoCompleteChange,
  ButtonPrimary,
  Form,
  InputSelect,
  InputText,
  useSnackbar
} from '@lambdacurry/component-library';
import { MJMLTemplateName } from 'lib/types/mjml';
import { useEmailPreviewQuery, useSendEmailPreviewMutation } from 'lib/home/generated/queries';
import { Container } from 'lib/components';
import { InputGrid } from '../../components';

export interface EmailPreviewProps {
  templateName?: string;
}

const initialValues = {
  to: '',
  templateName: MJMLTemplateName.Base
};

type FormValues = typeof initialValues;

export const EmailPreview: FC<EmailPreviewProps> = ({ templateName }) => {
  const { data } = useEmailPreviewQuery({ templateName });
  const { html = '' } = data?.email_preview || {};
  return <iframe title="Email preview" srcDoc={html} width="100%" height="100%" />;
};

const EmailPreviewPage: FC = () => {
  const { addSnackbar } = useSnackbar();
  const { mutate } = useSendEmailPreviewMutation({
    onSuccess: async () => await addSnackbar('Email sent!', { variant: 'success' })
  });
  const [templateName, setTemplateName] = useState(MJMLTemplateName.Base);
  const options = Object.keys(MJMLTemplateName).map(key => ({ label: key, value: MJMLTemplateName[key] }));

  const handleChange: AutoCompleteChange = (event, value) => setTemplateName(value);

  const handleSubmit = (values: FormValues, formikHelpers: FormikHelpers<FormValues>) =>
    mutate({ templateName: values.templateName, to: values.to });

  return (
    <div className="email-preview-page h-screen grid grid-cols-[1fr] grid-rows-[min-content_1fr]">
      <Form
        className="email-preview-page__form bg-white border-b border-gray-edge"
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <Container>
          <InputGrid className="!gap-4">
            <div className="col-span-12 md:col-span-5">
              <InputSelect
                label="Select a template to preview"
                name="templateName"
                onChange={handleChange}
                value={templateName}
                options={options}
                optionValueKey="value"
                inputProps={{ className: '!m-0' }}
              />
            </div>
            <div className="col-span-12 md:col-span-5">
              <InputText
                name="to"
                label="Email address(es)"
                placeholder="Enter a comma-separated list of email addresses"
                className="col-span-12 md:col-span-5"
              />
            </div>
            <div className="col-span-12 md:col-span-2">
              <ButtonPrimary type="submit">Send Test</ButtonPrimary>
            </div>
          </InputGrid>
        </Container>
      </Form>

      {templateName && (
        <div className="email-preview-page__viewer h-full">
          <EmailPreview templateName={templateName} />
        </div>
      )}
    </div>
  );
};

export default EmailPreviewPage;
